/**
 * MissionBriefing — Owner intelligence summary widget for the Command Center
 *
 *  - Loads automatically on mount
 *  - Refreshes when parent calls `refreshTick` change (wired to Refresh All)
 *  - Respects parent's auto-refresh checkbox via the same tick
 *  - Force-refresh on user click bypasses backend cache
 *  - Renders source badge (AI / FALLBACK / CACHED) + relative age
 *  - Never displays an error inline if the AI call fails — the backend
 *    already substituted a deterministic fallback
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Crosshair, RefreshCw, Loader2, Sparkles, Archive } from "lucide-react";
import { adminCommandMissionBriefing } from "../../lib/api";

const relAge = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

export const MissionBriefing = ({ opToken, userToken, refreshTick = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(false);
    try {
      const r = await adminCommandMissionBriefing(opToken, forceRefresh, userToken);
      setData(r);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [opToken, userToken]);

  // Initial load + parent refresh signal
  useEffect(() => { load(false); }, [load, refreshTick]);

  const tone = data?.source === "ai" ? "ai" : "fallback";
  const ageText = data?.generated_at ? relAge(data.generated_at) : "";

  return (
    <section
      data-testid="cmd-mission-briefing"
      className="relative border border-[#D92D20]/40 bg-gradient-to-br from-[#13100c] via-[#0F0F0F] to-[#0a0907] p-5 md:p-6 overflow-hidden"
    >
      {/* Subtle corner emblem */}
      <div className="absolute -top-6 -right-6 opacity-[0.08] pointer-events-none">
        <Crosshair size={96} className="text-[#D92D20]" />
      </div>

      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Crosshair size={13} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#D92D20]">
            // MISSION BRIEFING
          </span>
          {data?.source === "ai" && (
            <span
              data-testid="cmd-briefing-source-ai"
              className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.25em] text-[#E0A526] border border-[#E0A526]/50 px-1.5 py-0.5"
              title="Generated by AI analyst"
            >
              <Sparkles size={8} /> AI
            </span>
          )}
          {data?.source === "fallback" && (
            <span
              data-testid="cmd-briefing-source-fallback"
              className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.25em] text-[#757065] border border-[#454038] px-1.5 py-0.5"
              title="Deterministic fallback (AI unavailable)"
            >
              <Archive size={8} /> FALLBACK
            </span>
          )}
          {data?.cached && (
            <span
              data-testid="cmd-briefing-cached"
              className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.25em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-1.5 py-0.5"
              title="Served from cache (3-min TTL)"
            >
              CACHED
            </span>
          )}
        </div>
        <button
          data-testid="cmd-briefing-refresh"
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] disabled:opacity-50"
          title="Regenerate (bypass cache)"
        >
          {loading
            ? <Loader2 size={10} className="animate-spin" />
            : <RefreshCw size={10} />}
          Regen
        </button>
      </div>

      {/* Briefing body */}
      <div className="relative">
        {loading && !data ? (
          <div className="flex items-center gap-2 font-mono text-[10px] text-[#757065] italic">
            <Loader2 size={11} className="animate-spin" /> Compiling briefing…
          </div>
        ) : data?.briefing ? (
          <p
            data-testid="cmd-briefing-text"
            className="font-display text-[15px] md:text-[17px] leading-relaxed text-[#E4DFD3] tracking-[0.005em]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {data.briefing}
          </p>
        ) : error ? (
          <p className="font-mono text-[10px] text-[#757065] italic">
            // Briefing unavailable. Refresh to retry.
          </p>
        ) : null}

        {ageText && (
          <div className="mt-3 pt-2 border-t border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] flex items-center justify-between">
            <span>// CLASSIFIED · OWNER EYES ONLY</span>
            <span data-testid="cmd-briefing-age">generated {ageText}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default MissionBriefing;
